














































































































































import { Component, Vue, Ref, Watch, Prop } from "vue-property-decorator";
import { firestore } from "firebase/app";
import {
  ComBoxSensorUsage,
  ControlConfig,
  DryingMode,
  formatDryingMode,
  formatSocketMode,
  getDefaultSocketModesForDryingProgram,
  SocketMode,
} from "@/data/models/ControlConfig";

import NightModeParametersComponent from "@/components/combox/NightModeParameters.vue";
import { formatDayOfTheWeek, formatJsTime } from "@/utils/DateFormatter";
import PickerDialogComponent from "@/components/common/PickerDialog.vue";
import TimePickerComponent from "@/components/common/TimePicker.vue";
import StateButtonComponent, {
  ButtonState,
  getButtonStateClass,
  getButtonStateColor,
} from "@/components/common/StateButton.vue";
import { themedFill, themedFillTextClass } from "@/utils/Theme";
import { ComBoxDryingProgram } from "@/data/models/ComBoxDryingProgram";
import { ComBox, doesComBoxSupportAdditionalSensors, getComBoxDryingPrograms, getComBoxProgramName, getSensorTypes, isHyDryMini } from "@/data/models/ComBox";
import ConfirmationDialogComponent from "../common/ConfirmationDialog.vue";
import SensorUsageRecommendationComponent from "./SensorUsageRecommendation.vue";
import { getActiveUser } from "@/data/repository/UserRepository";
import { changeProgramDuringDryingAccess } from "@/permissions/UserPermissions";
import { canChangeDryingProgram } from "@/utils/combox/ControlConfigAccess";

export interface ControlConfigUpdate {
  newValue: ControlConfig;
  oldValue: ControlConfig;
  initialUpdate?: boolean;
}

@Component({
  components: {
    picker: PickerDialogComponent,
    "time-picker": TimePickerComponent,
    "state-button": StateButtonComponent,
    "night-mode-parameters": NightModeParametersComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    "sensor-usage-recommendation": SensorUsageRecommendationComponent,
  },
})
export default class ControlConfigComponent extends Vue {
  @Ref("nightModeParameters")
  nightModeParameters!: NightModeParametersComponent;

  @Ref("confirmationDialog")
  confirmationDialog!: ConfirmationDialogComponent;

  @Ref("sensorUsageRecommendation")
  sensorUsageRecommendation!: SensorUsageRecommendationComponent;

  @Ref("picker")
  picker!: PickerDialogComponent<number>;

  @Prop()
  selectableComBoxDryingPrograms!: ComBoxDryingProgram[];

  @Prop()
  selectableDryingPrograms!: number[];

  @Prop()
  comBox!: ComBox | null;

  @Prop()
  isClientConfigSettings!: boolean | null;

  @Prop({ default: 3 })
  defaultDryingProgramMini!: number;

  @Prop({ default: () => [3] })
  selectableDryingProgramsMiniOptions!: number[];

  didConfirmChange = false;
  oldValue: ControlConfig | null = null;

  get activeUser() {
    return getActiveUser();
  }

  get isAlwaysAllowedToChangeDryingProgram() {
    return changeProgramDuringDryingAccess(this.activeUser)
  }

  get canChangeDryingMode() {
    return canChangeDryingProgram({
      dryRunActive: this.comBox?.comBoxStatus?.dryRunActive ?? false,
      isMini: this.isMini,
      hasOverrideAccess: this.isAlwaysAllowedToChangeDryingProgram,
    });
  }

  get disabledDryingModeButtonText() {
    return this.dryingProgramFormatter(this.dryingProgram);
  }

  // Immediate: the dialog's content mounts with the list already set, so a plain watcher never
  // fired on first open and the picker showed the initial Box list (0 / 1 / 2) to a Mini.
  @Watch("selectableDryingPrograms", { immediate: true })
  onSelectableDryingProgramsChanged() {
    this.dryingProgramOptions = this.selectableDryingPrograms.map(
      (program) => program
    );

    if (!this.selectableDryingPrograms.includes(this.dryingProgram)) {
      this.dryingProgram = this.selectableDryingPrograms[0];
    }
  }

  showDisabledDryingModeHint() {
    this.confirmationDialog.show({
      title: this.$t(
        "comBoxDetail.controlConfig.dryingModeChangeDisabledDialogTitle"
      ).toString(),
      message: this.$t(
        "comBoxDetail.controlConfig.dryingModeChangeDisabledDialogMessage"
      ).toString(),
      rejectMessage: null,
      acceptMessage: this.$t("common.ok").toString(),
    });
  }

  confirmationMessage = "";

  //Disabled for now
  showDryingMode = false;

  resolve?: (value: ControlConfig) => void = undefined;
  reject?: (reason: string) => void = undefined;

  nightModeStart: firestore.Timestamp | null = (() => { const d = new Date(); d.setHours(22, 0, 0, 0); return firestore.Timestamp.fromDate(d); })();
  nightModeEnd: firestore.Timestamp | null = (() => { const d = new Date(); d.setHours(6, 0, 0, 0); return firestore.Timestamp.fromDate(d); })();

  get isMini() {
    return this.comBox ? isHyDryMini(this.comBox) : false;
  }

  get supportsAdditionalSensors() {
    if (!this.comBox) {
      return false;
    }

    return doesComBoxSupportAdditionalSensors(this.comBox);
  }

  get sensorTypes() {
    if (!this.comBox) {
      return [];
    }

    return getSensorTypes(this.comBox.comBoxStatus);
  }

  get totalNumberOfSensors() {
    return this.supportsAdditionalSensors ? 9 : 5;
  }

  sensorUsage =
    this.supportsAdditionalSensors
      ? Array(9).fill(ComBoxSensorUsage.PROGRAM).map((v, i) => (i === 4 ? ComBoxSensorUsage.REFERENCE : v))
      : [
          ComBoxSensorUsage.PROGRAM,
          ComBoxSensorUsage.PROGRAM,
          ComBoxSensorUsage.PROGRAM,
          ComBoxSensorUsage.PROGRAM,
          ComBoxSensorUsage.REFERENCE,
        ];

  dryingProgram = 0;
  hasNightModeParametersChanged = false;

  updateConfig = false;
  isDisplayingControlConfig = false;

  onHasChanged(hasChanged: boolean) {
    if (hasChanged) {
      this.$emit("hasChanged", [true]);
    }
  }

  async onNewDryingProgramPicked(newDryingProgram: number) {
    if (!this.isClientConfigSettings) {
      const oldDryingProgram = this.dryingProgram;
      const needsReferenceSensor = this.comBox
        ? getComBoxDryingPrograms(this.comBox, this).find((program) => program.programNumber === newDryingProgram)?.needsReferenceSensor ?? newDryingProgram === 2
        : newDryingProgram === 2;

      if (needsReferenceSensor) {
        const didConfirmChange = await this.sensorUsageRecommendation.show();
        this.didConfirmChange = didConfirmChange;
        this.dryingProgram = oldDryingProgram;

        if (this.didConfirmChange) {
          this.setNewDryingProgram(newDryingProgram);
        }
        else {
          this.picker.openPicker();
        }
      }
      else {
        this.setNewDryingProgram(newDryingProgram);
      }
    }
    else {
      this.setNewDryingProgram(newDryingProgram);
    }
  }

  onDefaultDryingProgramMiniPicked(program: number) {
    this.$emit("defaultDryingProgramMiniChanged", program);
    this.$emit("hasChanged", [true]);
  }

  setNewDryingProgram(newDryingProgram: number) {
    if (newDryingProgram !== this.dryingProgram) {
      this.dryingProgram = newDryingProgram;
      
      this.$emit("hasChanged", [true]);
    }

    const { socket1Mode, socket2Mode } = getDefaultSocketModesForDryingProgram(
      newDryingProgram
    );

    this.socket1Mode = socket1Mode;
    this.socket2Mode = socket2Mode;

    if (this.picker) {
      this.picker.isPickerOpen = false;
    }

    this.updateConfig = true;
  }

  onNightModeActiveChanged(nightModeActive: boolean) {
    if (nightModeActive !== this.nightModeActive) {
      this.$emit("hasChanged", [true]);
    }
  }

  dryingProgramOptions = [0, 1, 2];

  get dryingProgramFormatter() {
    return (dryingProgram: number) => {
      return getComBoxProgramName(
        this,
        dryingProgram,
        this.comBox ?? undefined
      );
    };
  }

  getDefaultProgramName(programNumber: number) {
    if (programNumber === 0) {
      return this.$t("comBoxDetail.monitor").toString();
    }

    if (programNumber === 1) {
      return this.$t("comBoxDetail.programDryingTime").toString();
    }

    if (programNumber === 2) {
      return this.$t("comBoxDetail.programDryingSensor").toString();
    }

    return this.$t("comBoxDetail.formatProgram", [programNumber]).toString();
  }

  get dryingProgramButtonState() {
    return this.dryingProgram === 3
      ? ButtonState.GREEN
      : ButtonState.YELLOW;
  }

  dryingMode = DryingMode.NEGATIVE_PRESSURE;
  dryingModeOptions = [
    DryingMode.NEGATIVE_PRESSURE,
    DryingMode.POSITIVE_PRESSURE,
    DryingMode.PUSH_PULL,
  ];

  get dryingModeFormatter() {
    return (dryingMode: DryingMode) => formatDryingMode(this, dryingMode);
  }

  get dryingModeButtonState() {
    return ButtonState.RED;
  }

  socket1Mode = SocketMode.ON;
  socket2Mode = SocketMode.ON;

  get socket1Label() {
    return this.isMini
      ? this.$t("comBoxDetail.controlConfig.socketA").toString()
      : this.$t("comBoxDetail.controlConfig.socketAB").toString();
  }

  get socket2Label() {
    return this.isMini
      ? this.$t("comBoxDetail.controlConfig.socketB").toString()
      : this.$t("comBoxDetail.controlConfig.socketCD").toString();
  }

  socketModeOptions = [SocketMode.PROGRAM, SocketMode.ON, SocketMode.OFF];

  get socket1ModeFormatterAlternate() {
    return (socketMode: SocketMode) =>
      formatSocketMode(this, socketMode);
  }

  get socket2ModeFormatterAlternate() {
    return (socketMode: SocketMode) =>
      formatSocketMode(this, socketMode);
  }

  get socket1ModeFormatter() {
    return (socketMode: SocketMode) =>
      this.socket1Label +
      ": " +
      formatSocketMode(this, socketMode);
  }

  get socket2ModeFormatter() {
    return (socketMode: SocketMode) =>
      this.socket2Label +
      ": " +
      formatSocketMode(this, socketMode);
  }

  get socket1ButtonState() {
    return this.getSocketModeButtonState(this.socket1Mode);
  }

  get socket2ButtonState() {
    return this.getSocketModeButtonState(this.socket2Mode);
  }

  getSocketModeButtonState(socketMode: SocketMode) {
    return socketMode === SocketMode.PROGRAM
      ? ButtonState.YELLOW
      : socketMode === SocketMode.OFF
        ? ButtonState.RED
        : ButtonState.GREEN;
  }

  nightModeActive = false;
  nightModeActiveOptions = [true, false];

  get nightModeActiveFormatter() {
    return (nightModeActive: boolean) => {
      return this.$t(nightModeActive ? "comBoxDetail.controlConfig.nightModeOn" : "comBoxDetail.controlConfig.nightModeOff").toString();
    };
  }

  get nightModeButtonState() {
    if (!this.nightModeActive) return ButtonState.RED;
    return this.nightModeAffectsAllSockets ? ButtonState.GREEN : ButtonState.YELLOW;
  }

  nightModeAffectsAllSockets = true;
  nightModeActiveDays: number[] = [];
  nightModeActiveDaysOptions = [0, 1, 2, 3, 4, 5, 6];

  displayControlConfig(controlConfig: ControlConfig) {
    this.isDisplayingControlConfig = true;

    this.dryingProgram = controlConfig.dryingProgram ?? this.selectableDryingPrograms[0];
    this.dryingMode = controlConfig.dryingMode;

    this.socket1Mode = controlConfig.socket1Mode;
    this.socket2Mode = controlConfig.socket2Mode;

    this.sensorUsage = controlConfig.sensorUsage;

    this.nightModeActive = controlConfig.nightModeActive;
    this.nightModeAffectsAllSockets = controlConfig.nightModeAffectsAllSockets;

    if (
      controlConfig.nightModeStartHour < 24 &&
      controlConfig.nightModeStartMinute < 60
    ) {
      const date = new Date();
      date.setHours(controlConfig.nightModeStartHour);
      date.setMinutes(controlConfig.nightModeStartMinute);

      this.nightModeStart = firestore.Timestamp.fromDate(date);
    }

    if (
      controlConfig.nightModeEndHour < 24 &&
      controlConfig.nightModeEndMinute < 60
    ) {
      const date = new Date();
      date.setHours(controlConfig.nightModeEndHour);
      date.setMinutes(controlConfig.nightModeEndMinute);

      this.nightModeEnd = firestore.Timestamp.fromDate(date);
    }

    const activeDays: number[] = [];

    controlConfig.nightModeActiveDays.forEach((value, index) => {
      if (value) {
        activeDays.push(index);
      }
    });

    this.nightModeActiveDays = activeDays;

    this.dryingProgramOptions = this.selectableDryingPrograms.map(
      (program) => program
    );

    // Snapshot the displayed config so the first emit has a correct old value.
    // Without this, the v-model update from the picker (which fires after
    // the async onNewDryingProgramPicked yields) corrupts Vue's cached
    // watcher oldValue, causing the parent to see old===new and skip
    // getDefaultSensorConfig on the first program change.
    this.oldValue = this.collectControlConfig();
    this.isDisplayingControlConfig = false;
  }

  get controlConfig() {
    return this.collectControlConfig();
  }

  @Watch("controlConfig")
  onControlConfigChanged(newValue: ControlConfig, oldValue: ControlConfig) {
    if (this.isDisplayingControlConfig) {
      return;
    }

    const previousValue = this.oldValue ?? oldValue;
    const hasChanged = JSON.stringify(newValue) !== JSON.stringify(previousValue);

    if (!hasChanged) {
      return;
    }

    if (this.isClientConfigSettings) {
      this.$emit("controlConfigChanged", {
        newValue: newValue,
        oldValue: previousValue,
      });
    }
    else {
      this.$emit("controlConfigChanged", {
        newValue: newValue,
        oldValue: previousValue,
        initialUpdate: false
      });
    }

    this.updateConfig = false;
    this.oldValue = newValue;
  }

  collectControlConfig(): ControlConfig {
    let nightModeStartHour = 0;
    let nightModeStartMinute = 0;

    const nightModeStartDate = this.nightModeStart?.toDate();
    if (nightModeStartDate) {
      nightModeStartHour = nightModeStartDate.getHours();
      nightModeStartMinute = nightModeStartDate.getMinutes();
    }

    let nightModeEndHour = 0;
    let nightModeEndMinute = 0;

    const nightModeEndDate = this.nightModeEnd?.toDate();
    if (nightModeEndDate) {
      nightModeEndHour = nightModeEndDate.getHours();
      nightModeEndMinute = nightModeEndDate.getMinutes();
    }

    return {
      socket1Mode: this.socket1Mode,
      socket2Mode: this.socket2Mode,
      dryingMode: this.dryingMode,
      dryingProgram: this.dryingProgram,
      sensorProgram: 0,
      sensorUsage: [] as number[],
      nightModeActive: this.nightModeActive,
      nightModeAffectsAllSockets: this.nightModeAffectsAllSockets,
      nightModeStartHour: nightModeStartHour,
      nightModeStartMinute: nightModeStartMinute,
      nightModeEndHour: nightModeEndHour,
      nightModeEndMinute: nightModeEndMinute,
      nightModeActiveDays: this.nigthModeSelectedDays,
    };
  }

  get nigthModeSelectedDays() {
    return this.nightModeActiveDaysOptions.map(
      (value) => this.nightModeActiveDays.indexOf(value) >= 0
    );
  }

  async onSetNightModeParameters() {
    const newParameters = await this.nightModeParameters.show(
      this.currentNightModeParameters,
      this.isClientConfigSettings ?? false
    );

    if (newParameters) {
      this.nightModeAffectsAllSockets =
        newParameters.nightModeAffectsAllSockets;
      this.nightModeActiveDays = newParameters.nightModeActiveDays;
      this.nightModeStart = newParameters.nightModeStart;
      this.nightModeEnd = newParameters.nightModeEnd;
    }
  }

  get currentNightModeParameters() {
    return {
      nightModeStart: this.nightModeStart,
      nightModeEnd: this.nightModeEnd,
      nightModeActiveDays: this.nightModeActiveDays,
      nightModeAffectsAllSockets: this.nightModeAffectsAllSockets,
    };
  }

  get nightModeParametersButtonState() {
    return this.currentNightModeParameters.nightModeAffectsAllSockets
      ? ButtonState.GREEN
      : ButtonState.YELLOW;
  }

  get nightModeButtonClass() {
    const state = this.nightModeParametersButtonState;

    return themedFillTextClass(getButtonStateColor(state), getButtonStateClass(state));
  }

  /** The night-mode card's fill: the dark palette shade of its state colour in dark mode. */
  get nightModeButtonColor() {
    return themedFill(getButtonStateColor(this.nightModeParametersButtonState));
  }

  get nightModeParametersDescription() {
    const currentNightModeParameters = this.currentNightModeParameters;

    const start = currentNightModeParameters.nightModeStart
      ? formatJsTime(currentNightModeParameters.nightModeStart.toDate())
      : "22:00";

    const end = currentNightModeParameters.nightModeEnd
      ? formatJsTime(currentNightModeParameters.nightModeEnd.toDate())
      : "06:00";

    const affectedSockets = this.$t(
      `comBoxDetail.controlConfig.nightModeSocketsOptions.${
        currentNightModeParameters.nightModeAffectsAllSockets
          ? this.isClientConfigSettings
              ? "allSocketsShortAlternate"
              : "allSocketsShort"
          : this.isClientConfigSettings
            ? "programSocketsShortAlternate"
            : "programSocketsShort"
          
      }`
    ).toString();

    const groupedDays: number[][] = [];
    let next: number[] = [];

    const isActiveDaysDefined = this.nigthModeSelectedDays.reduce((a,b) => a || b );

    this.nigthModeSelectedDays.map((isSelected, index) => {
      if (isSelected || !isActiveDaysDefined) {
        next.push(index);
      } else {
        if (next.length > 0) {
          groupedDays.push(next);
        }
        next = [];
      }
    });

    if (next.length > 0) {
      groupedDays.push(next);
    }

    const days = groupedDays
      .map((grouped) => {
        if (grouped.length > 2) {
          return `${formatDayOfTheWeek(this, grouped[0])}-${formatDayOfTheWeek(
            this,
            grouped[grouped.length - 1]
          )}`;
        } else {
          return grouped
            .map((value) => formatDayOfTheWeek(this, value))
            .join(", ");
        }
      })
      .join(", ");

    return this.isClientConfigSettings
      ? days !== "" 
        ? this.$t("comBoxDetail.controlConfig.formatNightModeParametersAlternate", [
            start,
            end,
            days,
            affectedSockets,
          ]).toString()
        : this.$t("comBoxDetail.controlConfig.formatNightModeParametersNoDaysAlternate", [
            start,
            end,
            affectedSockets,
          ]).toString()
      : this.$t("comBoxDetail.controlConfig.formatNightModeParameters", [
          start,
          end,
          days,
          affectedSockets,
        ]).toString();
  }
}
